import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import toast from 'react-hot-toast';
import {
  Check, Lock, ExternalLink, Globe, RotateCcw,
  Smartphone, Apple,
  Wand2, Sparkles, Loader2, Shield, Paintbrush, Star, ToggleLeft,
  Settings, Users, GraduationCap, Dumbbell, CalendarDays, ChevronRight,
  ImagePlus, EyeOff, Banknote, PieChart, SlidersHorizontal,
  CopyPlus, Archive, ArchiveRestore, AlertTriangle,
} from 'lucide-react';
import { ACADEMY_THEMES, DEFAULT_DESIGN_CONFIG } from '../constants/academyThemes';
import { THEME_SWATCH } from '../../contexts/ThemeContext';
import { academyAPI } from '../../services/api';
import AcademyBasicInfoForm, {
  EMPTY_ACADEMY_FORM, academyToFormData, buildAcademyPayload, validateAcademyForm, mergePendingAdditions,
} from '../components/AcademyBasicInfoForm';
import StaffSettings from '../tabs/settings/StaffSettings';
import StudentsSettings from '../tabs/settings/StudentsSettings';
import CourtsSettings from '../tabs/settings/CourtsSettings';
import StaffScheduleSettings from '../tabs/settings/StaffScheduleSettings';
import ProgramsSettings from '../tabs/settings/ProgramsSettings';
import GallerySettings from '../tabs/settings/GallerySettings';
import PersonalizationSettings from '../tabs/settings/PersonalizationSettings';
import PayoutsSettings from '../../payments/components/PaymentPayoutsSettings';
import PaymentDeskSettings from '../../payments/components/PaymentDeskSettings';
import PaymentReportsTabs from '../../payments/reports/PaymentReportsTabs';
import AcademyTimetable from '../components/AcademyTimetable';

const FONT_PREVIEW = {
  modern: 'system-ui, -apple-system, sans-serif',
  classic: "Georgia, 'Times New Roman', serif",
  sporty: "'Arial Black', Arial, sans-serif"
};

// ─── Toggle ────────────────────────────────────────────────────────────────────

function Toggle({ enabled, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0"
      style={{
        backgroundColor: disabled ? '#d1d5db' : enabled ? '#2563eb' : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
      disabled={disabled}
    >
      <span
        className="inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform"
        style={{ transform: enabled ? 'translateX(24px)' : 'translateX(4px)' }}
      />
    </button>
  );
}

// ─── Theme Preview ─────────────────────────────────────────────────────────────

function ThemePreview({ theme }) {
  if (!theme) return null;
  return (
    <div className="rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-white" style={{ maxWidth: 340 }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: theme.primary }}>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
          <span className="text-white text-sm font-bold">Academy Name</span>
        </div>
        <div className="px-3 py-1 rounded-lg text-xs font-bold" style={{ backgroundColor: '#ffffff', color: theme.primary }}>
          Enroll
        </div>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: theme.primary }}>Featured Class</div>
          <div className="h-2 w-32 rounded" style={{ backgroundColor: theme.primaryLight }} />
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-1.5 rounded-lg text-xs font-bold text-white" style={{ backgroundColor: theme.primary }}>Enroll Now</button>
          <button className="px-4 py-1.5 rounded-lg text-xs font-bold border-2" style={{ borderColor: theme.primary, color: theme.primary }}>Learn More</button>
        </div>
        <div className="rounded-lg p-3 text-xs" style={{ backgroundColor: theme.primaryLight, color: theme.primaryDark }}>
          Premium coaching • World-class facilities • All skill levels welcome
        </div>
      </div>
      <div className="px-4 py-3" style={{ backgroundColor: theme.primaryDark }}>
        <div className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>Powered by Kynaix Sports</div>
      </div>
    </div>
  );
}

// ─── Mini Hero Preview ─────────────────────────────────────────────────────────

// ─── Mini Card Preview ─────────────────────────────────────────────────────────

// ─── Font Preview ──────────────────────────────────────────────────────────────

function FontPreview({ style, large = false }) {
  return (
    <div
      className="w-full rounded-lg flex flex-col items-center justify-center"
      style={{ fontFamily: FONT_PREVIEW[style], backgroundColor: '#f9fafb', minHeight: large ? 80 : 48, padding: large ? '16px 12px' : '8px 12px' }}
    >
      <div style={{ fontSize: large ? 28 : 18, fontWeight: 'bold', color: '#111827', lineHeight: 1.1 }}>Aa</div>
      {large && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 6, letterSpacing: '0.02em' }}>The quick brown fox</div>}
    </div>
  );
}

// ─── Style Selector ────────────────────────────────────────────────────────────

function StyleSelector({ label, value, onChange, options, renderPreview }) {
  const current = options.find(o => o.id === value) || options[0];
  const others = options.filter(o => o.id !== value);

  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-3">{label}</p>

      {/* Active option — prominent */}
      <div className="rounded-xl overflow-hidden border-2 mb-3" style={{ borderColor: '#16a34a' }}>
        <div className="flex items-center justify-between px-4 py-2" style={{ backgroundColor: '#16a34a' }}>
          <div className="flex items-center gap-1.5">
            <Check size={12} color="white" />
            <span className="text-white text-xs font-bold uppercase tracking-wider">Active</span>
          </div>
          <span className="text-white text-xs font-semibold opacity-90">{current.label}</span>
        </div>
        <div className="p-4 bg-white">
          {renderPreview(current.id, true)}
          <p className="text-xs text-gray-400 mt-2">{current.desc}</p>
        </div>
      </div>

      {/* Alternatives */}
      {others.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Switch to</p>
          <div className="grid grid-cols-2 gap-3">
            {others.map(opt => (
              <button
                key={opt.id}
                onClick={() => onChange(opt.id)}
                className="text-left rounded-xl border-2 overflow-hidden transition-all hover:border-blue-400 hover:shadow-md"
                style={{ borderColor: '#e5e7eb' }}
              >
                <div className="p-3 bg-white">
                  {renderPreview(opt.id, false)}
                  <div className="mt-2 font-semibold text-sm text-gray-800">{opt.label}</div>
                  <div className="text-xs text-gray-400 mb-2.5">{opt.desc}</div>
                  <div className="w-full py-1.5 rounded-lg text-xs font-bold text-center text-white" style={{ backgroundColor: '#2563eb' }}>
                    Switch
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Design Proposal Card ──────────────────────────────────────────────────────

function DesignProposalCard({ proposal, isApplied, onApply, onTryOut }) {
  const pt = ACADEMY_THEMES.find(t => t.id === proposal.themeId) || ACADEMY_THEMES[0];

  return (
    <div
      className="rounded-xl overflow-hidden transition-all"
      style={{
        border: isApplied ? `2px solid ${pt.primary}` : '2px solid #e5e7eb',
        boxShadow: isApplied ? `0 0 0 3px ${pt.primaryLight}` : '0 1px 3px rgba(0,0,0,0.07)'
      }}
    >
      {/* Mini Nav */}
      <div className="h-8 flex items-center px-2.5 gap-1.5" style={{ backgroundColor: pt.primary }}>
        <div className="w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }} />
        <div className="h-2 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.4)', width: 56 }} />
        <div className="ml-auto h-5 w-12 rounded text-[8px] font-bold flex items-center justify-center" style={{ backgroundColor: '#fff', color: pt.primary }}>
          Enroll
        </div>
      </div>

      {/* Mini Hero */}
      {proposal.heroStyle === 'fullscreen' && (
        <div className="h-20 relative" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=300)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="absolute inset-0" style={{ background: `linear-gradient(rgba(0,0,0,0.35), ${pt.primary}bb)` }} />
          <div className="relative p-2.5 flex flex-col justify-end h-full">
            <div className="h-2 w-20 rounded mb-1" style={{ backgroundColor: 'rgba(255,255,255,0.85)' }} />
            <div className="h-1.5 w-14 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }} />
          </div>
        </div>
      )}
      {proposal.heroStyle === 'split' && (
        <div className="h-20 flex">
          <div className="w-1/2 p-2 flex flex-col justify-center" style={{ backgroundColor: pt.primary }}>
            <div className="h-2 w-14 rounded mb-1" style={{ backgroundColor: 'rgba(255,255,255,0.85)' }} />
            <div className="h-1.5 w-10 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }} />
          </div>
          <div className="w-1/2" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=200)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        </div>
      )}
      {proposal.heroStyle === 'minimal' && (
        <div className="h-20 flex items-center justify-center" style={{ backgroundColor: pt.primary }}>
          <div className="text-center px-2">
            <div className="h-2 w-20 rounded mx-auto mb-1" style={{ backgroundColor: 'rgba(255,255,255,0.85)' }} />
            <div className="h-1.5 w-14 rounded mx-auto" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }} />
          </div>
        </div>
      )}

      {/* Mini Content Cards */}
      <div className="p-2.5 space-y-1.5" style={{ backgroundColor: '#f9fafb' }}>
        {[1, 2].map(i => (
          <div
            key={i}
            className="h-8 rounded-lg flex items-center px-2.5 gap-2"
            style={
              proposal.cardStyle === 'elevated'
                ? { backgroundColor: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }
                : proposal.cardStyle === 'bordered'
                ? { backgroundColor: '#fff', border: `1.5px solid ${pt.primary}` }
                : { backgroundColor: pt.primaryLight }
            }
          >
            <div className="h-1.5 flex-1 rounded bg-gray-200" />
            <div className="h-5 w-10 rounded text-[8px] font-bold flex items-center justify-center text-white flex-shrink-0" style={{ backgroundColor: pt.primary }}>
              Enroll
            </div>
          </div>
        ))}
      </div>

      {/* Label + Actions */}
      <div className="px-3 pb-3 pt-2 bg-white space-y-2">
        <div>
          <div className="font-bold text-sm text-gray-900 mb-0.5">{proposal.name}</div>
          <div className="text-xs" style={{ color: pt.primary }}>{proposal.tag}</div>
        </div>
        <button
          onClick={onTryOut}
          className="w-full py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-80 border-2"
          style={{ borderColor: pt.primary, color: pt.primary, backgroundColor: 'transparent' }}
        >
          Try Out
        </button>
        <button
          onClick={onApply}
          className="w-full py-2 rounded-lg text-xs font-bold transition-all hover:opacity-90"
          style={isApplied
            ? { backgroundColor: pt.primaryLight, color: pt.primary }
            : { backgroundColor: pt.primary, color: '#ffffff' }
          }
        >
          {isApplied ? '✓ Applied' : 'Apply This Design'}
        </button>
      </div>
    </div>
  );
}

// ─── Build Proposals ───────────────────────────────────────────────────────────

// ─── Section Card ──────────────────────────────────────────────────────────────

function SectionCard({ title, children, theme }) {
  return (
    <div className={`${theme.card} rounded-xl border p-6 shadow-sm`}>
      <h3 className={`text-lg font-bold ${theme.text} mb-5`}>{title}</h3>
      {children}
    </div>
  );
}

// ─── Save Button ───────────────────────────────────────────────────────────────

function SaveButton({ onClick }) {
  // ⚠️ THIS WAS AN INLINE HEX — style={{ backgroundColor: '#2563eb' }} — so no
  // amount of sweeping Tailwind classes was ever going to find it, and it stayed
  // Kynaix blue on an orange academy long after everything around it moved.
  const { theme } = useTheme();

  return (
    <div className="flex justify-end">
      <button
        onClick={onClick}
        className={`px-8 py-3 rounded-xl font-bold text-sm transition-all hover:opacity-90 shadow-md ${theme.buttonPrimary}`}
      >
        Save Portal Settings
      </button>
    </div>
  );
}

// ─── AcademySettingsTab ────────────────────────────────────────────────────────

// Each item's requiredCapability gates it against myCapabilities (see
// AcademySettingsTab below) — owner/admin (isAdmin=true) bypass this entirely.
const SETTINGS_NAV = [
  {
    group: 'General',
    icon: Settings,
    items: [
      { id: 'basic',    label: 'Basic',          requiredCapability: 'manage_content' },
      { id: 'advanced', label: 'Advanced',       requiredCapability: 'manage_content' },
      { id: 'features', label: 'Features',       icon: ToggleLeft, requiredCapability: 'manage_features' },
      { id: 'design',   label: 'Design Studio',  icon: Wand2,  requiredCapability: 'manage_content' },
      // Not built. It sits in the nav so the idea stays visible and the answer
      // is honest, rather than a section inside Design Studio that looked
      // finished and generated three canned results from a hardcoded list.
      { id: 'ai-design', label: 'AI Design Studio', icon: Sparkles, requiredCapability: 'manage_content' },
      // Sits directly above Publish, and below Design Studio: the order is the
      // order the owner works in — style the page, decide what goes on it, then
      // publish it. Same capability as publishing, since it decides what
      // publishing discloses.
      { id: 'personalization', label: 'Personalization', icon: SlidersHorizontal, requiredCapability: 'manage_content' },
      { id: 'publish',  label: 'Publish to Web', icon: Globe,  requiredCapability: 'manage_content' },
      // Last in General, and owner-only: both actions are about the academy as
      // a whole rather than anything in it, and neither is something to hand to
      // a coach with manage_content.
      { id: 'copy-archive', label: 'Copy & Archive', icon: CopyPlus, adminOnly: true },
    ],
  },
  {
    group: 'Manage',
    icon: Users,
    items: [
      { id: 'manage-staff',     label: 'Staff',           icon: Users,         requiredCapability: 'manage_staff' },
      { id: 'manage-students',  label: 'Members',         icon: GraduationCap, requiredCapability: 'manage_members' },
      { id: 'manage-programs',  label: 'Programs',        icon: Star,          requiredCapability: 'manage_programs' },
      { id: 'manage-courts',    label: 'Courts & Fields', icon: Dumbbell,      requiredCapability: 'manage_courts' },
      { id: 'manage-schedule',  label: 'Staff Schedule',  icon: CalendarDays,  requiredCapability: 'manage_staff' },
      { id: 'manage-timetable', label: 'Schedule',        icon: CalendarDays,  requiredCapability: 'manage_programs' },
      { id: 'manage-gallery',   label: 'Gallery',         icon: ImagePlus,     requiredCapability: 'manage_content' },
    ],
  },
  {
    group: 'Finance',
    icon: Banknote,
    items: [
      // First in Finance, and the only item here a coach can reach: it is the
      // one somebody uses during opening hours, with a player standing in front
      // of them. Reports and payouts are back-office, read after the fact.
      //
      // Each of these now matches exactly what its endpoints check, so a door
      // that opens is a door that works. They used to ride on manage_programs
      // (the till) or be adminOnly (the reports) — the first was too generous,
      // the second too strict to delegate at all.
      { id: 'manage-desk',         label: 'Front Desk',   icon: Banknote, requiredCapability: 'manage_front_desk' },
      { id: 'manage-reports',      label: 'Reports',      icon: PieChart, requiredCapability: 'view_reports' },
      // Named for what it opens. This entry renders the payouts screen — where
      // the academy connects its own Stripe account and sees what it is owed —
      // and it was labelled "Transactions", so the one door to getting paid was
      // behind a word nobody would click for that. It also answered to
      // view_reports, which let anyone granted Reports start bank onboarding
      // for the academy; connecting a bank account is not a reporting action.
      { id: 'manage-payouts',      label: 'Payouts',      icon: Banknote, adminOnly: true },
    ],
  },
];


/**
 * What this academy runs.
 *
 * Turning something off removes it from the product for this academy —
 * navigation, screens and the endpoints behind them — rather than hiding a
 * button over a door that still opens. Each says plainly what happens, because
 * "Enable Feeds" alone does not tell an owner what they are about to lose.
 */
const FEATURE_ROWS = [
  { key: 'enableCourtBookings', label: 'Court Bookings',
    desc: 'Members can hire courts. Off hides Courts and refuses new bookings; existing ones are untouched.' },
  { key: 'enableOpenPlay', label: 'Open Play',
    desc: 'Drop-in nights people book one at a time. Off hides Open Play and stops new open-play offerings.' },
  { key: 'enableMeetups', label: 'Meetups',
    desc: 'Free community sessions with their own squad. Off stops new meetups being created.' },
  { key: 'enableOnlineBooking', label: 'Online Card Payments',
    desc: 'Off removes the card option at checkout — cash, check and Venmo at the counter only.' },
  { key: 'enableSelfRefund', label: 'Online Card Refunds by the Member',
    desc: 'Members giving up a paid place get their card refunded automatically. Off still lets them '
        + 'leave, but the money is held and listed at the Front Desk for you to action. Staff refunds '
        + 'are unaffected.' },
  { key: 'enableFeeds', label: 'Feeds',
    desc: 'The academy\'s posts and announcements. Off hides the Feeds tab.' },
  { key: 'enableChats', label: 'Chats',
    desc: 'Messaging between members and staff. Off hides the chat bubble and messaging entry points.' },
  { key: 'enableTournaments', label: 'Tournaments',
    desc: 'Competitions your academy hosts. Off hides the Tournaments tab and stops new ones being created.' },
  { key: 'enableKynaixLink', label: 'Kynaix Sports Link',
    desc: 'The "Kynaix Sports" link in your header. Off removes it, so your academy page stands on its own.' },
  // The only flag that starts OFF. Copying an academy is a heavier thing than
  // the rest of this list — it writes a second academy — so it is something an
  // owner turns on when they want it, not something waiting to be clicked.
  { key: 'enableCopy', label: 'Allow Copying',
    desc: 'Lets you duplicate this academy\'s setup into a new one — programs, courts, prices and settings, '
        + 'never members, bookings or payments. Off refuses the copy outright.' },
];

function FeaturesPanel({ academyId, theme }) {
  const [flags, setFlags] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);   // the flag awaiting confirmation
  const [blocked, setBlocked] = useState(null);   // a flag the server won't take yet, and why

  useEffect(() => {
    academyAPI.getById(academyId)
      .then(a => setFlags(a?.features || {}))
      .catch(() => toast.error('Could not load features'));
  }, [academyId]);

  /**
   * Save, then reload.
   *
   * The navigation is built from the academy this page loaded with, so a flag
   * saved here does not move it — Open Play stayed in the menu after being
   * switched off, which looks like the switch did nothing. Rather than thread
   * a refresh through every screen that reads a flag, the page reloads once
   * and everything is rebuilt from the saved state.
   *
   * The reload keeps `settings=features` in the URL, so it comes back here
   * rather than dumping the owner on Basic.
   */
  const applyAndReload = async (key, value) => {
    setSaving(true);
    try {
      await academyAPI.update(academyId, { features: { [key]: value } });
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'settings');
      url.searchParams.set('settings', 'features');
      window.location.replace(url.toString());
    } catch (err) {
      setSaving(false);
      setPending(null);
      // The API says exactly what is wrong and what to do about it — "Online
      // payments need the platform fee agreement signed first" — and this
      // used to replace all of it with "Could not save that change", leaving
      // an owner to guess at a blocker the server had already explained.
      const message = err?.message || 'Could not save that change';
      // One refusal is not really an error: the change is fine, a step just
      // has to happen first. That deserves a way to go and do it rather than
      // a toast that names a screen and then disappears.
      if (err?.data?.code === 'AGREEMENT_REQUIRED') { setBlocked({ key, message }); return; }
      toast.error(message);
    }
  };

  const toggle = (key) => {
    const turningOff = flags[key] !== false;
    // Turning something OFF takes things away from an academy's members, so it
    // asks first. Turning it back on takes nothing away and just happens.
    if (turningOff) { setPending(key); return; }
    applyAndReload(key, true);
  };

  if (!flags) return <div className={`${theme.card} rounded-xl border p-8 text-center ${theme.textMuted}`}>Loading…</div>;

  const pendingRow = pending ? FEATURE_ROWS.find(r => r.key === pending) : null;

  return (
    <div className={`${theme.card} rounded-xl border p-6`}>
      <h3 className={`text-lg font-bold ${theme.text}`}>Features</h3>
      <p className={`text-sm ${theme.textSecondary} mt-1 mb-5`}>
        Switch off what your academy does not run. Changes apply immediately.
      </p>
      {pendingRow && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
             onClick={() => setPending(null)}>
          <div className={`${theme.card} w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3`}
               onClick={(e) => e.stopPropagation()}>
            <h3 className={`text-lg font-bold ${theme.text}`}>Turn off {pendingRow.label}?</h3>
            <p className={`text-sm ${theme.textSecondary}`}>{pendingRow.desc}</p>
            <p className={`text-sm ${theme.textMuted}`}>
              The page will reload so your academy reflects the change. You can turn it back on at any time.
            </p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => applyAndReload(pendingRow.key, false)}
                disabled={saving}
                className={`flex-1 px-4 py-2.5 rounded-xl ${theme.buttonDanger || 'bg-pink-500 text-white'} text-sm font-semibold disabled:opacity-60`}
              >
                {saving ? 'Turning off…' : `Turn off ${pendingRow.label}`}
              </button>
              <button
                onClick={() => setPending(null)}
                disabled={saving}
                className={`flex-1 px-4 py-2.5 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium`}
              >
                Keep it on
              </button>
            </div>
          </div>
        </div>
      )}

      {blocked && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
             onClick={() => setBlocked(null)}>
          <div className={`${theme.card} w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3`}
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className={`text-lg font-bold ${theme.text}`}>One step first</h3>
                <p className={`text-sm ${theme.textSecondary} mt-1`}>{blocked.message}</p>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => {
                  const url = new URL(window.location.href);
                  url.searchParams.set('tab', 'settings');
                  url.searchParams.set('settings', 'manage-payouts');
                  window.location.assign(url.toString());
                }}
                className={`flex-1 px-4 py-2.5 rounded-xl ${theme.button || theme.tabActive} text-sm font-semibold`}
              >
                Open Payouts
              </button>
              <button
                onClick={() => setBlocked(null)}
                className={`flex-1 px-4 py-2.5 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium`}
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {FEATURE_ROWS.map(row => {
          const on = flags[row.key] !== false;
          return (
            <div key={row.key} className={`flex items-start gap-4 p-4 rounded-xl border ${theme.border}`}>
              <div className="min-w-0 flex-1">
                <p className={`font-semibold ${theme.text}`}>{row.label}</p>
                <p className={`text-sm ${theme.textSecondary} mt-0.5`}>{row.desc}</p>
              </div>
              <button
                onClick={() => toggle(row.key)}
                disabled={saving}
                role="switch"
                aria-checked={on}
                aria-label={row.label}
                className={`relative w-12 h-6 rounded-full flex-shrink-0 transition-colors disabled:opacity-50 ${
                  on ? theme.switchOn : 'bg-gray-400 dark:bg-gray-600'
                }`}
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? 'left-6' : 'left-0.5'}`} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * What a copy brings, in the owner's words.
 *
 * Each line is what the owner gets, not what the table is called. "Courts &
 * Fields" and "Court Schedule" are separate because copying the courts without
 * the hours they open is a common thing to want — a new site, same layout,
 * different timetable — and copying 1,700 slots by accident is not.
 */
const COPY_PARTS = [
  { key: 'programs',     label: 'Programs',          desc: 'Every program with its prices, questions and waivers.' },
  { key: 'tournaments',  label: 'Future Tournaments', desc: 'Tournaments yet to start. Past ones stay behind.' },
  { key: 'courts',       label: 'Courts & Fields',   desc: 'The courts themselves, with their hire rates.' },
  { key: 'courtEvents',  label: 'Court Schedule',    desc: 'The open-play and hire slots on those courts.' },
  { key: 'featureFlags', label: 'Feature Settings',  desc: 'Which features the new academy runs.' },
  { key: 'staff',        label: 'Staff & Permissions', desc: 'Your staff list and what each of them may do.' },
  { key: 'gallery',      label: 'Gallery Images',    desc: 'Photos, copied across in storage.' },
];

/**
 * Copy this academy, or put it away.
 *
 * Two things an owner does to an academy as a whole rather than to anything
 * inside it, so they sit together and away from the day-to-day settings.
 *
 * Both are owner-only. Archiving in particular is not a delete: nothing is
 * destroyed and the owner can bring it back, which is the reason it exists —
 * an academy that has finished its season should stop appearing to members
 * without anyone having to decide whether to erase its history.
 */
function CopyArchivePanel({ academyId, theme }) {
  const [academy, setAcademy] = useState(null);
  const [name, setName] = useState('');
  const [parts, setParts] = useState(() =>
    Object.fromEntries(COPY_PARTS.map(p => [p.key, true])));
  const [copying, setCopying] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [working, setWorking] = useState(false);

  const load = () => academyAPI.getById(academyId)
    .then(a => setAcademy(a?.academy || a))
    .catch(() => toast.error('Could not load this academy'));
  useEffect(() => { load(); }, [academyId]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!academy) {
    return <div className={`${theme.card} rounded-xl border p-8 text-center ${theme.textMuted}`}>Loading…</div>;
  }

  const canCopy   = academy.features?.enableCopy === true;
  const archived  = Boolean(academy.archivedAt);
  const chosen    = COPY_PARTS.filter(p => parts[p.key]).length;

  const runCopy = async () => {
    const trimmed = name.trim();
    if (!trimmed) { toast.error('Give the new academy a name'); return; }
    setCopying(true);
    try {
      const created = await academyAPI.copy(academyId, { name: trimmed, parts });
      toast.success(`"${created.name}" created`);
      // Straight into the new one — the next thing an owner does is edit it,
      // and leaving them on the original with a toast makes them go and find it.
      //
      // A full load rather than navigate(): this page keeps the chosen settings
      // panel in state seeded once from the URL, so a client-side hop landed on
      // the new academy still showing Copy & Archive — as if nothing had moved.
      window.location.assign(`/academy/${created.id}?tab=settings&settings=basic`);
    } catch (err) {
      // The API's own words: it knows whether the name is taken, whether
      // copying is switched off, or whether this is not the owner's academy.
      toast.error(err?.message || 'Could not copy this academy');
      setCopying(false);
    }
  };

  const setArchived = async (next) => {
    setWorking(true);
    try {
      await (next ? academyAPI.archive(academyId) : academyAPI.unarchive(academyId));
      toast.success(next ? 'Academy archived' : 'Academy restored');
      setConfirmArchive(false);
      await load();
    } catch (err) {
      toast.error(err?.message || 'Could not change that');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ─── Copy ─────────────────────────────────────────────────────── */}
      <div className={`${theme.card} rounded-xl border p-6`}>
        <div className="flex items-start gap-3">
          <CopyPlus size={20} className={theme.textSecondary} />
          <div className="min-w-0">
            <h3 className={`text-lg font-bold ${theme.text}`}>Copy this academy</h3>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>
              Creates a second academy with the same setup, owned by you. Members,
              bookings and payments are never copied — the new one starts empty.
            </p>
          </div>
        </div>

        {!canCopy ? (
          <div className={`mt-4 rounded-xl border ${theme.border} p-4 flex items-start gap-3`}>
            <Lock size={16} className={`${theme.textMuted} mt-0.5 flex-shrink-0`} />
            <p className={`text-sm ${theme.textSecondary}`}>
              Copying is switched off for this academy. Turn on{' '}
              <strong className={theme.text}>Allow Copying</strong> under{' '}
              <strong className={theme.text}>Features</strong> first.
            </p>
          </div>
        ) : (
          <>
            <div className="mt-5">
              <label className={`block text-sm font-medium ${theme.text} mb-1.5`} htmlFor="copy-academy-name">
                Name of the new academy
              </label>
              <input
                id="copy-academy-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={`${academy.name} — 2nd Location`}
                maxLength={200}
                className={`w-full px-3 py-2.5 rounded-xl border ${theme.border} ${theme.input || ''} ${theme.text} text-sm`}
              />
              <p className={`text-xs ${theme.textMuted} mt-1.5`}>
                Must not match an academy that already exists.
              </p>
            </div>

            <p className={`text-sm font-medium ${theme.text} mt-5 mb-2`}>What to copy</p>
            <div className="space-y-2">
              {COPY_PARTS.map(part => (
                <label
                  key={part.key}
                  className={`flex items-start gap-3 p-3 rounded-xl border ${theme.border} cursor-pointer`}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(parts[part.key])}
                    onChange={(e) => setParts(prev => ({ ...prev, [part.key]: e.target.checked }))}
                    className="mt-1 w-4 h-4 flex-shrink-0 accent-blue-600"
                  />
                  <span className="min-w-0">
                    <span className={`block text-sm font-semibold ${theme.text}`}>{part.label}</span>
                    <span className={`block text-sm ${theme.textSecondary}`}>{part.desc}</span>
                  </span>
                </label>
              ))}
            </div>

            <button
              onClick={runCopy}
              disabled={copying || !name.trim()}
              className={`mt-5 w-full sm:w-auto px-5 py-2.5 rounded-xl ${theme.button || theme.tabActive} text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2`}
            >
              {copying
                ? <><Loader2 size={16} className="animate-spin" /> Copying…</>
                : <><CopyPlus size={16} /> Create the copy</>}
            </button>
            {copying && (
              <p className={`text-xs ${theme.textMuted} mt-2`}>
                A large academy can take a minute. Leaving this page will not stop it.
              </p>
            )}
            <p className={`text-xs ${theme.textMuted} mt-2`}>
              {chosen === 0
                ? 'Nothing selected — this creates an empty academy with your details on it.'
                : `${chosen} of ${COPY_PARTS.length} selected.`}
            </p>
          </>
        )}
      </div>

      {/* ─── Archive ──────────────────────────────────────────────────── */}
      <div className={`${theme.card} rounded-xl border p-6`}>
        <div className="flex items-start gap-3">
          {archived ? <ArchiveRestore size={20} className={theme.textSecondary} />
                    : <Archive size={20} className={theme.textSecondary} />}
          <div className="min-w-0">
            <h3 className={`text-lg font-bold ${theme.text}`}>
              {archived ? 'This academy is archived' : 'Archive this academy'}
            </h3>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>
              {archived
                ? 'It is hidden from everyone else and is not published. You can find it under '
                  + '"Show archived" in My Academies, and restore it whenever you like.'
                : 'Hides it from members and from Discover, and unpublishes its public page. '
                  + 'Nothing is deleted, and you can restore it at any time.'}
            </p>
          </div>
        </div>

        <button
          onClick={() => (archived ? setArchived(false) : setConfirmArchive(true))}
          disabled={working}
          className={archived
            ? `mt-4 w-full sm:w-auto px-5 py-2.5 rounded-xl ${theme.button || theme.tabActive} text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2`
            : `mt-4 w-full sm:w-auto px-5 py-2.5 rounded-xl border ${theme.border} ${theme.text} text-sm font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2`}
        >
          {working
            ? <><Loader2 size={16} className="animate-spin" /> Working…</>
            : archived
              ? <><ArchiveRestore size={16} /> Restore this academy</>
              : <><Archive size={16} /> Archive this academy</>}
        </button>
      </div>

      {confirmArchive && (
        <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
             onClick={() => setConfirmArchive(false)}>
          <div className={`${theme.card} w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3`}
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className={`text-lg font-bold ${theme.text}`}>Archive {academy.name}?</h3>
                <p className={`text-sm ${theme.textSecondary} mt-1`}>
                  Members will no longer see it and its public page comes down.
                  Existing bookings and records are kept, and you can restore it later.
                </p>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => setArchived(true)}
                disabled={working}
                className={`flex-1 px-4 py-2.5 rounded-xl ${theme.buttonDanger || 'bg-pink-500 text-white'} text-sm font-semibold disabled:opacity-60`}
              >
                {working ? 'Archiving…' : 'Archive it'}
              </button>
              <button
                onClick={() => setConfirmArchive(false)}
                disabled={working}
                className={`flex-1 px-4 py-2.5 rounded-xl border ${theme.border} ${theme.text} text-sm font-medium`}
              >
                Keep it active
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const AcademySettingsTab = ({ academyId, userRole, isAdmin = false, myCapabilities = [], onAcademyUpdate }) => {
  const navigate = useNavigate();
  const { theme, themes, previewAcademyTheme } = useTheme();

  // Owner/admin bypass entirely; everyone else needs the specific capability
  // granted via the Staff Settings permissions matrix (default-deny).
  const canAccess = (item) => item.adminOnly
    ? isAdmin
    : isAdmin || myCapabilities.includes(item.requiredCapability);
  const allowedNav = SETTINGS_NAV
    .map(group => ({ ...group, items: group.items.filter(canAccess) }))
    .filter(group => group.items.length > 0);

  // Only route access is filtered here (nav renders/links only to allowed
  // tabs) — the backend independently re-checks every capability on every
  // mutating request regardless of what the UI shows, so this isn't the real
  // security boundary, just keeps the UI from offering doors that 403 anyway.
  // Deep-linkable, so a link can land on a specific panel — a "back to
  // reports" button is useless if it returns you to Basic. Validated against
  // allowedNav so a URL cannot open a panel this user has no access to.
  // On a phone the nav and the panel are stacked, so choosing an item changed
  // something below the fold and read as "nothing happened". The list collapses
  // to a single bar after a choice; tapping it brings the list back.
  const [navOpen, setNavOpen] = useState(false);
  const contentRef = useRef(null);

  const requestedTab = new URLSearchParams(window.location.search).get('settings');
  const [settingsTab, setSettingsTab] = useState(() => {
    const reachable = allowedNav.flatMap(g => g.items.map(i => i.id));
    if (requestedTab && reachable.includes(requestedTab)) return requestedTab;
    return allowedNav[0]?.items[0]?.id || 'basic';
  });

  // What the collapsed bar says it is showing. Falls back to "Settings" rather
  // than going blank if a tab id ever outlives its nav entry.
  const currentNavLabel = allowedNav
    .flatMap(g => g.items)
    .find(i => i.id === settingsTab)?.label || 'Settings';
  // Design Studio config — backed by the real per-academy design-config API, not the
  // global kynaix_portal_elite localStorage key (that key still backs the standalone
  // /elite demo route only, see academyThemes.js DEFAULT_PORTAL_CONFIG).
  const [config, setConfig] = useState(() => ({ ...DEFAULT_DESIGN_CONFIG }));
  const [savingDesign, setSavingDesign] = useState(false);
  const [previewTheme, setPreviewTheme] = useState(null);

  // Basic info (including hero/background image) — backed by the actual academy
  // record, and the exact same shape/fields as CreateEditAcademyPage's formData —
  // see AcademyBasicInfoForm, the shared implementation both pages render.
  const [basicForm, setBasicForm] = useState(EMPTY_ACADEMY_FORM);
  const basicFormRef = useRef(null);
  // Only populated when the owner actively picks a new suggestion this session — left
  // undefined otherwise so handleSaveBasic doesn't re-upsert/overwrite the Addresses
  // row (with nulled-out lat/lng etc.) on every save if the address wasn't touched.
  const [selectedAddressDetails, setSelectedAddressDetails] = useState(null);
  const [sportTypes, setSportTypes] = useState([]);
  const [savingBasic, setSavingBasic] = useState(false);
  const [resettingBasic, setResettingBasic] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [publishedAt, setPublishedAt] = useState(null);
  const [publicUrl, setPublicUrl] = useState(null);

  useEffect(() => {
    import('../../services/api').then(({ sportTypeAPI: api }) =>
      api.getAll().then(res => setSportTypes(res?.data || res || [])).catch(() => {})
    );
  }, []);

  // Shared by the initial load and the Reset button — re-fetches the academy
  // record and discards any unsaved local edits in basicForm.
  const loadBasicFromDb = () => {
    if (!academyId) return Promise.resolve();
    return academyAPI.getById(academyId).then(a => {
      setBasicForm(academyToFormData(a));
      setSelectedAddressDetails(null);
      setPublishedAt(a?.publishedAt || null);
      setPublicUrl(a?.publicUrl || null);
    });
  };

  useEffect(() => {
    loadBasicFromDb().catch(() => {});
  }, [academyId]);

  const handleResetBasic = async () => {
    setResettingBasic(true);
    try {
      await loadBasicFromDb();
      // Discard any typed-but-not-"+ Add"ed offering/highlight text too — Reset
      // means discard all unsaved edits, not just the ones already committed to formData.
      basicFormRef.current?.clearPendingInputs();
      toast.success('Reloaded from last saved version');
    } catch {
      toast.error('Failed to reload academy info');
    } finally {
      setResettingBasic(false);
    }
  };

  useEffect(() => {
    if (!academyId) return;
    academyAPI.getDesignConfig(academyId)
      .then(dc => {
        setConfig(c => ({ ...c, ...dc }));
        // What is actually stored, so "previewing" can mean something and
        // leaving without saving can put it back.
        setSavedThemeKey(dc?.themeKey || null);
      })
      .catch(() => {});
  }, [academyId]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const result = await academyAPI.publish(academyId);
      setPublishedAt(result.publishedAt);
      setPublicUrl(result.publicUrl);
      toast.success('Academy page published!');
    } catch (err) {
      toast.error('Failed to publish. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setUnpublishing(true);
    try {
      await academyAPI.unpublish(academyId);
      setPublishedAt(null);
      setPublicUrl(null);
      toast.success('Academy page unpublished.');
    } catch (err) {
      toast.error('Failed to unpublish. Please try again.');
    } finally {
      setUnpublishing(false);
    }
  };

  const setBasic = (key, value) => setBasicForm(f => ({ ...f, [key]: value }));

  const handleHoursChange = (type, field, value) =>
    setBasicForm(f => ({ ...f, [type]: { ...f[type], [field]: value } }));

  const handleAddressSelect = (addressData) => {
    setBasic('address', addressData.fullAddress || '');
    setSelectedAddressDetails(addressData);
  };

  const handleSaveBasic = async () => {
    const effectiveBasicForm = mergePendingAdditions(basicForm, basicFormRef.current?.getPendingAdditions());
    const error = validateAcademyForm(effectiveBasicForm);
    if (error) { toast.error(error); return; }
    setSavingBasic(true);
    try {
      const payload = buildAcademyPayload(effectiveBasicForm, selectedAddressDetails);
      await academyAPI.update(academyId, payload);
      // Reflect the normalized (trimmed) slug back, and the offerings/highlights
      // that getPendingAdditions() folded in, so the UI matches what was actually
      // saved instead of leaving stale typed-but-uncommitted text in the inputs.
      setBasicForm({ ...effectiveBasicForm, slug: payload.slug });
      basicFormRef.current?.clearPendingInputs();
      toast.success('Academy info saved');
      onAcademyUpdate?.();
    } catch (err) {
      // Surface the backend's specific message (e.g. "An academy with this URL slug
      // already exists", 409) instead of a generic failure for every error.
      toast.error(err?.message || 'Failed to save academy info');
    } finally {
      setSavingBasic(false);
    }
  };

  useEffect(() => {
    const found = ACADEMY_THEMES.find(t => t.id === config.themeId) || ACADEMY_THEMES[2];
    setPreviewTheme(found);
  }, [config.themeId]);

  const set = (key, value) => setConfig(c => ({ ...c, [key]: value }));

  /**
   * Choosing a theme shows it immediately, on this screen and every other page
   * of the academy — picking a colour from a swatch and guessing is no way to
   * choose one. It is only a preview until Save: nobody else sees it, and
   * leaving without saving restores what was stored.
   */
  const [savedThemeKey, setSavedThemeKey] = useState(null);
  const previewTheme_ = (key) => { set('themeKey', key); previewAcademyTheme?.(key); };
  const themeDirty = Boolean(config.themeKey) && config.themeKey !== savedThemeKey;

  /**
   * Leaving drops an UNSAVED preview only.
   *
   * A saved theme has to stay on screen: the scope above took its value when
   * the page loaded and does not refetch, so clearing the preview after a save
   * would snap the academy back to the theme it had a minute ago and look like
   * the save failed. The ref is how the cleanup sees the latest values rather
   * than the ones captured when it was created.
   */
  const themeStateRef = useRef({ chosen: null, saved: null });
  themeStateRef.current = { chosen: config.themeKey || null, saved: savedThemeKey };
  useEffect(() => () => {
    const { chosen, saved } = themeStateRef.current;
    if (chosen !== saved) previewAcademyTheme?.(null);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const setFeature = (key, value) => setConfig(c => ({ ...c, features: { ...c.features, [key]: value } }));

  const handleSave = async () => {
    setSavingDesign(true);
    try {
      const saved = await academyAPI.updateDesignConfig(academyId, {
        themeKey:     config.themeKey,
        themeId:      config.themeId,
        heroStyle:    config.heroStyle,
        cardStyle:    config.cardStyle,
        fontPairing:  config.fontPairing,
        brandingMode: config.brandingMode,
        tagline:      config.tagline,
      });
      setConfig(c => ({ ...c, ...saved }));
      // What was a preview is now everyone's.
      // Saved: it is the academy's theme now, so the preview has nothing left
      // to override and the scope reads the stored value.
      setSavedThemeKey(saved.themeKey || config.themeKey || null);

      toast.success('Theme saved — every visitor sees it now. Publish to Web to update your public site.');
    } catch {
      toast.error('Failed to save design settings');
    } finally {
      setSavingDesign(false);
    }
  };

  return (
    <div className="flex flex-col md:flex-row min-h-[600px]">

      {/* ── Sidebar ── */}
      <div className={`w-full md:w-52 flex-shrink-0 border-b md:border-b-0 md:border-r ${theme.border} pb-2 md:pb-0 md:pr-2 py-2`}>
        {/* Phone only. The desktop sidebar always shows the whole list — there
            is room for it there, and collapsing it would be a regression. */}
        <button
          type="button"
          onClick={() => setNavOpen(v => !v)}
          className={`md:hidden w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border ${theme.border} ${theme.cardHover} mb-2`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Settings size={15} className={theme.textMuted} />
            <span className={`text-sm font-semibold truncate ${theme.text}`}>{currentNavLabel}</span>
          </span>
          <ChevronRight
            size={16}
            className={`${theme.textMuted} flex-shrink-0 transition-transform ${navOpen ? 'rotate-90' : ''}`}
          />
        </button>

        <div className={`${navOpen ? '' : 'hidden'} md:block`}>
        {allowedNav.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.group} className="mb-4">
              <div className={`flex items-center gap-2 px-3 py-1.5 mb-1`}>
                <GroupIcon size={13} className={theme.textMuted} />
                <span className={`text-xs font-semibold uppercase tracking-wider ${theme.textMuted}`}>
                  {group.group}
                </span>
              </div>
              {group.items.map(item => {
                const Icon = item.icon;
                const isActive = settingsTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setSettingsTab(item.id);
                      setNavOpen(false);
                      // The panel starts below the nav on a phone; without this
                      // the collapse alone can still leave it off screen.
                      requestAnimationFrame(() => contentRef.current?.scrollIntoView({
                        behavior: 'smooth', block: 'start',
                      }));
                    }}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors mb-0.5 ${
                      isActive
                        ? `${theme.tabActive} font-semibold`
                        : `${theme.textSecondary} ${theme.cardHover}`
                    }`}
                  >
                    {Icon && <Icon size={15} className="flex-shrink-0" />}
                    <span className="flex-1 text-left">{item.label}</span>
                    {isActive && <ChevronRight size={14} className="flex-shrink-0 opacity-50" />}
                  </button>
                );
              })}
            </div>
          );
        })}
        </div>
      </div>

      {/* ── Content ── */}
      <div ref={contentRef} className="flex-1 pt-4 md:pt-0 md:pl-6 pb-20 min-w-0">
        {/* manage sections render full-width components */}
        {settingsTab === 'manage-staff' && (
          <StaffSettings academyId={academyId} userRole={userRole} isAdmin={isAdmin} myCapabilities={myCapabilities} onAcademyUpdate={onAcademyUpdate} />
        )}
        {settingsTab === 'manage-students' && (
          <StudentsSettings academyId={academyId} userRole={userRole} academyName="" onAcademyUpdate={onAcademyUpdate} />
        )}
        {settingsTab === 'manage-programs' && (
          <ProgramsSettings academyId={academyId} userRole={userRole} onAcademyUpdate={onAcademyUpdate} />
        )}
        {settingsTab === 'manage-courts' && (
          <CourtsSettings academyId={academyId} userRole={userRole} />
        )}
        {settingsTab === 'manage-schedule' && (
          <StaffScheduleSettings academyId={academyId} userRole={userRole} />
        )}
        {settingsTab === 'manage-timetable' && (
          <AcademyTimetable academyId={academyId} />
        )}
        {settingsTab === 'manage-gallery' && (
          <GallerySettings academyId={academyId} />
        )}
        {settingsTab === 'personalization' && (
          <PersonalizationSettings
            academyId={academyId}
            onGoToPublish={() => setSettingsTab('publish')}
          />
        )}

        {settingsTab === 'manage-desk' && (
          <PaymentDeskSettings academyId={academyId} />
        )}

        {/* Two views of the same money, deliberately separate: Reports answers
            "how are we doing?" with figures, Transactions answers "what was
            this line?" with rows. Both owner-agnostic components mounted with
            the academy as the host, rather than second implementations. */}
        {settingsTab === 'manage-reports' && (
          <div className="space-y-8">
            {/* ONE reports screen. Everything is a tab inside it — the page used
                to stack three separate report blocks, each with its own heading
                and its own date picker, answering overlapping questions. */}
            <PaymentReportsTabs academyId={academyId} />
          </div>
        )}

        {settingsTab === 'manage-payouts' && (
          <PayoutsSettings
            ownerType="academy"
            ownerId={academyId}
            // Back to THIS panel, not the top of Settings — and flagged so the
            // screen can report what actually happened at Stripe.
            returnPath={`/academy/${academyId}?tab=settings&settings=manage-payouts&connect=return`}
          />
        )}

        {settingsTab === 'publish' && (
          <div>
            <h3 className={`text-lg font-semibold ${theme.text} mb-1`}>Publish to Web</h3>
            <p className={`text-sm ${theme.textMuted} mb-6`}>
              Generate a public page for your academy visible to anyone — even without signing in.
              Re-publish anytime to reflect the latest info, programs, gallery, and staff.
            </p>
            <div className={`${theme.card} border ${theme.border} rounded-xl p-6 space-y-4`}>
              {publishedAt ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className={`text-sm font-medium ${theme.text}`}>Published</span>
                    <span className={`text-xs ${theme.textMuted}`}>· Last updated {new Date(publishedAt).toLocaleString()}</span>
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${theme.backgroundSecondary}`}>
                    <Globe size={14} className={`${theme.link} flex-shrink-0`} />
                    <a
                      href={`/academy/${basicForm.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-sm ${theme.link} hover:underline truncate flex-1`}
                    >
                      {window.location.origin}/academy/{basicForm.slug}
                    </a>
                    <a
                      href={`/academy/${basicForm.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex-shrink-0 ${theme.textMuted} ${theme.linkHover}`}
                    >
                      <ExternalLink size={13} />
                    </a>
                  </div>
                </>
              ) : (
                <p className={`text-sm ${theme.textMuted}`}>Not published yet. Click below to publish.</p>
              )}
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePublish}
                  disabled={publishing || unpublishing}
                  className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition"
                >
                  {publishing ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />}
                  {publishing ? 'Publishing…' : publishedAt ? 'Re-publish' : 'Publish to Web'}
                </button>
                {publishedAt && (
                  <button
                    onClick={handleUnpublish}
                    disabled={publishing || unpublishing}
                    className="flex items-center gap-2 px-5 py-2.5 border border-red-500/50 hover:bg-red-500/10 disabled:opacity-50 text-red-500 rounded-lg text-sm font-semibold transition"
                  >
                    {unpublishing ? <Loader2 size={14} className="animate-spin" /> : <EyeOff size={14} />}
                    {unpublishing ? 'Unpublishing…' : 'Unpublish'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

      {/* ══════════════════════════════════════
          BASIC
      ══════════════════════════════════════ */}
      {settingsTab === 'basic' && (
        <>
          <AcademyBasicInfoForm
            ref={basicFormRef}
            formData={basicForm}
            onChange={setBasic}
            onHoursChange={handleHoursChange}
            onAddressSelect={handleAddressSelect}
            sportTypes={sportTypes}
            theme={theme}
            showSlugWarning={!!publishedAt}
          />

          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSaveBasic}
              disabled={savingBasic || resettingBasic}
              className={`flex items-center gap-2 px-6 py-2.5 ${theme.buttonPrimary} rounded-lg text-sm font-semibold  transition disabled:opacity-50`}
            >
              {savingBasic ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              {savingBasic ? 'Saving…' : 'Save Academy Info'}
            </button>
            <button
              onClick={handleResetBasic}
              disabled={savingBasic || resettingBasic}
              title="Discard unsaved changes and reload from the last saved version"
              className={`flex items-center gap-2 px-6 py-2.5 border ${theme.border} ${theme.text} rounded-lg text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 transition disabled:opacity-50`}
            >
              {resettingBasic ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
              {resettingBasic ? 'Reloading…' : 'Reset'}
            </button>
          </div>
        </>
      )}

      {/* ══════════════════════════════════════
          ADVANCED
      ══════════════════════════════════════ */}
      {settingsTab === 'advanced' && (
        <>
          {/* Portal Branding */}
          <SectionCard title="Portal Branding" theme={theme}>
            <p className={`text-sm ${theme.textMuted} mb-4`}>
              Choose how your academy portal is presented to members and visitors.
            </p>
            <div className="space-y-3">
              {[
                {
                  id: 'kynaix',
                  icon: Shield,
                  label: 'Default Kynaix Sports',
                  desc: 'Use Kynaix Sports branding as-is. No setup needed — your portal is ready out of the box.',
                  features: ['Kynaix Sports logo & colors', 'Standard portal layout', 'Zero configuration required'],
                  badge: null,
                  badgeStyle: null,
                  viewUrl: '/academy/elite?branding=default'
                },
                {
                  id: 'personalized',
                  icon: Paintbrush,
                  label: 'Personalize within Kynaix Sports',
                  desc: 'Add your academy name, logo, tagline and contact info. Still powered by Kynaix Sports.',
                  features: ['Your academy name & logo', 'Custom tagline & about section', 'Contact & social links', 'Powered by Kynaix Sports'],
                  badge: 'Included',
                  badgeStyle: { backgroundColor: '#dcfce7', color: '#166534' },
                  viewUrl: '/academy/elite'
                },
                {
                  id: 'full',
                  icon: Wand2,
                  label: 'Full Personal Portal',
                  desc: 'Complete white-label experience. Use the Design Studio to build a portal that is entirely yours.',
                  features: ['Custom theme, colors & typography', 'Custom hero layouts & card styles', 'AI Design Studio access', 'No Kynaix branding shown'],
                  badge: 'Design Studio',
                  badgeStyle: { backgroundColor: '#ede9fe', color: '#7c3aed' },
                  viewUrl: '/elite'
                }
              ].map(opt => {
                const Icon = opt.icon;
                const isActive = (config.brandingMode || 'personalized') === opt.id;
                return (
                  <div
                    key={opt.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => set('brandingMode', opt.id)}
                    onKeyDown={e => e.key === 'Enter' && set('brandingMode', opt.id)}
                    className={`w-full text-left rounded-xl border-2 transition-all overflow-hidden cursor-pointer ${
                      isActive ? `${theme.borderAccent} ${theme.backgroundSecondary} ring-2 ring-blue-200` : `${theme.border} ${theme.card}`
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* Radio dot */}
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isActive ? `${theme.borderAccent} ${theme.tabActive}` : theme.borderSecondary
                          }`}
                        >
                          {isActive && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <Icon size={15} className={`flex-shrink-0 ${isActive ? theme.link : theme.textMuted}`} />
                            <span className={`font-bold text-sm ${isActive ? theme.link : theme.text}`}>{opt.label}</span>
                            {opt.badge && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={opt.badgeStyle}>{opt.badge}</span>
                            )}
                            <button
                              onClick={e => { e.stopPropagation(); window.open(opt.viewUrl, '_blank'); }}
                              className={`ml-auto flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all hover:opacity-80 flex-shrink-0 bg-transparent ${
                                isActive ? `${theme.borderAccent} ${theme.link}` : `${theme.borderSecondary} ${theme.textMuted}`
                              }`}
                            >
                              <ExternalLink size={11} /> View
                            </button>
                          </div>
                          <p className={`text-xs leading-relaxed mb-2.5 ${theme.textMuted}`}>{opt.desc}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1">
                            {opt.features.map(f => (
                              <div key={f} className="flex items-center gap-1">
                                <Check size={11} className={`flex-shrink-0 ${isActive ? theme.link : theme.textMuted}`} />
                                <span className={`text-xs ${isActive ? theme.link : theme.textMuted}`}>{f}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    {opt.id === 'full' && isActive && (
                      <div className="px-4 py-2.5 flex items-center justify-between bg-blue-100 border-t border-blue-200">
                        <span className={`text-xs font-medium ${theme.link}`}>Configure your portal look &amp; feel in Design Studio</span>
                        <button
                          onClick={e => { e.stopPropagation(); setSettingsTab('design'); }}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90 ${theme.tabActive}`}
                        >
                          <Wand2 size={12} /> Open Design Studio
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </SectionCard>

          {/* Portal Features */}
          <SectionCard title="Portal Features" theme={theme}>
            <div className="space-y-3">
              {[
                { key: 'feed', label: 'Feed / News', desc: 'Show a news feed on your portal' },
                { key: 'squads', label: 'Squads', desc: 'Display your squads and teams' },
                { key: 'gallery', label: 'Gallery', desc: 'Show a photo gallery tab' }
              ].map(f => (
                <div key={f.key} className="flex items-center justify-between py-2">
                  <div>
                    <div className={`text-sm font-medium ${theme.text}`}>{f.label}</div>
                    <div className={`text-xs ${theme.textMuted}`}>{f.desc}</div>
                  </div>
                  <Toggle enabled={config.features?.[f.key] ?? false} onChange={v => setFeature(f.key, v)} />
                </div>
              ))}
              <div className="flex items-center justify-between py-2 opacity-60">
                <div className="flex items-center gap-2">
                  <Lock size={14} className={theme.textMuted} />
                  <div>
                    <div className={`text-sm font-medium ${theme.text}`}>Marketplace</div>
                    <div className={`text-xs ${theme.textMuted}`}>Not available for academy portals</div>
                  </div>
                </div>
                <Toggle enabled={false} onChange={() => {}} disabled />
              </div>
            </div>
          </SectionCard>

          {/* Branded Apps (Premium) */}
          <div className={`${theme.card} rounded-xl border p-6 shadow-sm relative overflow-hidden`}>
            <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(to right, #f59e0b, #d97706, #b45309)' }} />
            <div className="flex items-center gap-3 mb-5">
              <h3 className={`text-lg font-bold ${theme.text}`}>Branded Apps</h3>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>Premium</span>
            </div>
            <div className="space-y-4">
              {[
                { icon: <Apple size={22} />, label: 'Branded iOS App', desc: 'Your academy, your brand, on the App Store' },
                { icon: <Smartphone size={22} />, label: 'Branded Android App', desc: 'Your academy, your brand, on Google Play' }
              ].map(item => (
                <div key={item.label} className={`flex items-center justify-between p-4 rounded-xl ${theme.backgroundSecondary}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${theme.border} border`}>
                      <span className={theme.textMuted}>{item.icon}</span>
                    </div>
                    <div>
                      <div className={`font-semibold text-sm ${theme.text}`}>{item.label}</div>
                      <div className={`text-xs ${theme.textMuted}`}>{item.desc}</div>
                    </div>
                  </div>
                  <Lock size={16} className={theme.textMuted} />
                </div>
              ))}
            </div>
            <button
              onClick={() => toast.success('Our team will reach out with pricing and details!')}
              className="mt-5 px-6 py-2.5 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(to right, #f59e0b, #d97706)' }}
            >
              Contact Sales
            </button>
          </div>

          <SaveButton onClick={handleSave} />
        </>
      )}

      {/* ══════════════════════════════════════
          DESIGN STUDIO
      ══════════════════════════════════════ */}
      {settingsTab === 'design' && (
        <>
          {/* Theme */}
          {/* One theme, the same list the profile menu offers.
              Twenty accent swatches decided a button colour on the published
              page and nothing else — the academy's own screens ignored them
              entirely. A theme means the whole palette here, applied to every
              page inside the academy and to the published site, so what an
              owner picks is what everybody sees. */}
          <SectionCard title="Academy Theme" theme={theme}>
            <p className={`text-sm ${theme.textSecondary} mb-4`}>
              Applies to every page inside your academy and to your published
              site. Visitors see your theme while they are here, whatever they
              picked for themselves.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
              {themes.map(t => {
                const on = (config.themeKey || DEFAULT_DESIGN_CONFIG.themeKey) === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => previewTheme_(t.key)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition ${
                      on ? `${theme.borderAccent} ${theme.tabActive}/10` : `${theme.border} ${theme.cardHover}`
                    }`}
                  >
                    <span className="w-3.5 h-3.5 rounded-full flex-shrink-0"
                          style={{ background: THEME_SWATCH[t.key] || '#3b82f6' }} />
                    <span className={`text-sm font-medium ${theme.text}`}>{t.name}</span>
                    {on && <Check size={16} className={`ml-auto ${theme.link}`} />}
                  </button>
                );
              })}
            </div>
            {themeDirty && (
              <div className={`rounded-xl border ${theme.border} px-4 py-3 text-sm ${theme.textSecondary} flex items-start gap-2`}>
                <Check size={16} className={`${theme.link} flex-shrink-0 mt-0.5`} />
                <span>
                  <strong className={theme.text}>Previewing this theme.</strong>{' '}
                  Save to apply it for everyone who visits your academy, then use{' '}
                  <strong className={theme.text}>Publish to Web</strong> to update your public site.
                </span>
              </div>
            )}
          </SectionCard>

          {/* Layout & Style is gone.
              Hero layout, card style and font pairing were three more choices
              that only ever reached the published page, and only in ways the
              owner could not see while choosing them. The academy picks a theme
              now; the rest is ours to get right. The stored values still render
              on the published site at their defaults, so nothing an academy has
              already published changes. */}


          <SaveButton onClick={handleSave} />
        </>
      )}
      {settingsTab === 'features' && (
        <FeaturesPanel academyId={academyId} theme={theme} />
      )}
      {settingsTab === 'copy-archive' && (
        <CopyArchivePanel academyId={academyId} theme={theme} />
      )}

      {settingsTab === 'ai-design' && (
        <div className={`${theme.card} rounded-xl border p-8 text-center`}>
          <Sparkles size={28} className="mx-auto mb-3" style={{ color: '#7c3aed' }} />
          <h3 className={`text-lg font-bold ${theme.text}`}>AI Design Studio</h3>
          <p className={`text-sm ${theme.textSecondary} mt-2 max-w-md mx-auto`}>
            Describe your academy and get a portal design back — theme, layout and
            styling chosen for you. It is an upcoming feature and not available yet.
          </p>
          <p className={`text-sm ${theme.textMuted} mt-4`}>
            Until then, pick a theme in <strong className={theme.text}>Design Studio</strong>.
          </p>
        </div>
      )}
      </div>{/* content panel */}
    </div>
  );
};

export default AcademySettingsTab;
